import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { isHelpdeskMode } from '../../utils/helpdeskMode';
import {
  Box,
  Typography,
  Paper,
  Avatar,
  Button,
  TextField,
  IconButton,
  Chip,
  Select,
  MenuItem,
  FormControl,
  CircularProgress,
  Alert,
  Snackbar,
  Divider,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Send as SendIcon,
  Lock as InternalIcon,
  PriorityHigh as UrgentIcon,
  KeyboardArrowUp as HighIcon,
  Remove as MediumIcon,
  KeyboardArrowDown as LowIcon,
  FiberManualRecord as DotIcon,
} from '@mui/icons-material';
import { format, formatDistanceToNow, parseISO } from 'date-fns';
import api, { extractData } from '../../utils/api';
import { useAuthStore } from '../../stores/authStore';

interface Ticket {
  id: string;
  ticketNumber: string;
  subject: string;
  description: string;
  category: string;
  priority: string;
  status: string;
  userId: string | null;
  organizationId: number;
  assignedTo: string | null;
  requesterEmail: string | null;
  requesterName: string | null;
  isExternal: boolean;
  resolvedAt: string | null;
  closedAt: string | null;
  createdAt: string;
  updatedAt: string;
  userEmail?: string;
  userName?: string;
  assignedEmail?: string;
  assignedName?: string;
  replyCount?: number;
  replies?: TicketReply[];
  attachments?: any[];
  metadata?: any;
}

interface TicketReply {
  id: string;
  ticketId: string;
  userId: string | null;
  message: string;
  isStaffReply: boolean;
  isInternalNote: boolean;
  senderEmail: string | null;
  senderName: string | null;
  createdAt: string;
  userEmail?: string;
  userName?: string;
}

const STATUS_COLORS: Record<string, string> = {
  open: '#3b82f6',
  in_progress: '#f59e0b',
  resolved: '#10b981',
  closed: '#6b7280',
};

const STATUS_LABELS: Record<string, string> = {
  open: 'Open',
  in_progress: 'In Progress',
  resolved: 'Resolved',
  closed: 'Closed',
};

const PRIORITY_COLORS: Record<string, string> = {
  urgent: '#ef4444',
  high: '#f97316',
  medium: '#f59e0b',
  low: '#22c55e',
};

const getPriorityIcon = (priority: string) => {
  switch (priority) {
    case 'urgent': return <UrgentIcon sx={{ fontSize: 14 }} />;
    case 'high': return <HighIcon sx={{ fontSize: 14 }} />;
    case 'medium': return <MediumIcon sx={{ fontSize: 14 }} />;
    case 'low': return <LowIcon sx={{ fontSize: 14 }} />;
    default: return null;
  }
};

const CATEGORIES = ['general', 'billing', 'technical', 'account'];

const HelpdeskTicketDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const basePath = isHelpdeskMode() ? '/support' : '/helpdesk';
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';
  // API always uses /support, basePath is only for UI routing
  const ticketsPath = isAdmin ? '/support/tickets' : '/support/my-tickets';

  const [ticket, setTicket] = useState<Ticket | null>(null);
  const [loading, setLoading] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [replying, setReplying] = useState(false);
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({ open: false, message: '', severity: 'success' });

  const loadTicket = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await api.get(`${ticketsPath}/${id}`);
      setTicket(extractData<Ticket>(res));
    } catch {
      setSnackbar({ open: true, message: 'Failed to load ticket', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [id, ticketsPath]);

  useEffect(() => { loadTicket(); }, [loadTicket]);

  const handleUpdate = async (updates: Record<string, string>) => {
    if (!ticket) return;
    try {
      await api.put(`${ticketsPath}/${ticket.id}`, updates);
      setSnackbar({ open: true, message: 'Ticket updated', severity: 'success' });
      loadTicket();
    } catch {
      setSnackbar({ open: true, message: 'Failed to update ticket', severity: 'error' });
    }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !ticket) return;
    setReplying(true);
    try {
      await api.post(`${ticketsPath}/${ticket.id}/replies`, {
        message: replyText.trim(),
        ...(isAdmin ? { isInternalNote } : {}),
      });
      setReplyText('');
      setIsInternalNote(false);
      setSnackbar({ open: true, message: isInternalNote ? 'Internal note added' : 'Reply sent', severity: 'success' });
      loadTicket();
    } catch {
      setSnackbar({ open: true, message: 'Failed to send reply', severity: 'error' });
    } finally {
      setReplying(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (!ticket) {
    return (
      <Box sx={{ textAlign: 'center', pt: 8 }}>
        <Typography variant="h6" sx={{ color: '#9ca3af' }}>Ticket not found</Typography>
        <Button onClick={() => navigate(`${basePath}/tickets`)} sx={{ mt: 2, textTransform: 'none' }}>
          Back to Tickets
        </Button>
      </Box>
    );
  }

  const replies = ticket.replies || [];

  // Build a timeline: original description + all replies, sorted by createdAt
  const timelineItems = [
    {
      id: 'original',
      type: 'description' as const,
      message: ticket.description,
      senderName: ticket.userName || ticket.requesterName || ticket.requesterEmail || 'User',
      isStaffReply: false,
      isInternalNote: false,
      createdAt: ticket.createdAt,
    },
    ...replies.map((r) => ({
      id: r.id,
      type: 'reply' as const,
      message: r.message,
      senderName: r.userName || r.senderName || r.userEmail || 'Staff',
      isStaffReply: r.isStaffReply,
      isInternalNote: r.isInternalNote,
      createdAt: r.createdAt,
    })),
  ];

  return (
    <Box sx={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/* Main Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Top Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2.5,
            py: 1.5,
            bgcolor: '#fff',
            borderBottom: '1px solid #e5e7eb',
          }}
        >
          <IconButton size="small" onClick={() => navigate(`${basePath}/tickets`)} sx={{ color: '#6b7280' }}>
            <BackIcon fontSize="small" />
          </IconButton>
          <Box sx={{ flex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontFamily: 'monospace', fontSize: '0.8rem', color: '#6366f1', fontWeight: 600 }}>
                {ticket.ticketNumber}
              </Typography>
              <Chip
                size="small"
                label={STATUS_LABELS[ticket.status] || ticket.status}
                sx={{
                  height: 22,
                  fontSize: '0.7rem',
                  fontWeight: 600,
                  bgcolor: `${STATUS_COLORS[ticket.status] || '#9e9e9e'}15`,
                  color: STATUS_COLORS[ticket.status] || '#9e9e9e',
                }}
              />
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: PRIORITY_COLORS[ticket.priority] }}>
                {getPriorityIcon(ticket.priority)}
                <Typography variant="caption" fontWeight={500} sx={{ fontSize: '0.75rem' }}>
                  {ticket.priority?.charAt(0).toUpperCase() + ticket.priority?.slice(1)}
                </Typography>
              </Box>
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#1f2937', lineHeight: 1.3, mt: 0.25 }}>
              {ticket.subject}
            </Typography>
          </Box>
        </Box>

        {/* Timeline */}
        <Box sx={{ flex: 1, overflow: 'auto', px: 3, py: 2.5, bgcolor: '#f9fafb' }}>
          {timelineItems.map((item, idx) => {
            const isFirst = idx === 0;
            const isNote = item.isInternalNote;
            const isStaff = item.isStaffReply;

            return (
              <Box key={item.id} sx={{ mb: 2, position: 'relative' }}>
                {/* Vertical connector line */}
                {idx > 0 && (
                  <Box sx={{ position: 'absolute', top: -16, left: 18, width: 2, height: 16, bgcolor: '#e5e7eb' }} />
                )}
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    borderRadius: '10px',
                    border: '1px solid',
                    borderColor: isNote ? '#fbbf24' : isFirst ? '#bfdbfe' : '#e5e7eb',
                    bgcolor: isNote ? '#fffbeb' : isStaff ? '#eff6ff' : '#fff',
                  }}
                >
                  {/* Sender bar */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                    <Avatar
                      sx={{
                        width: 30,
                        height: 30,
                        fontSize: '0.75rem',
                        bgcolor: isFirst ? '#3b82f6' : isStaff ? '#6366f1' : '#9ca3af',
                      }}
                    >
                      {item.senderName.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.85rem', color: '#1f2937' }}>
                        {item.senderName}
                      </Typography>
                    </Box>
                    {isStaff && !isNote && (
                      <Chip size="small" label="Staff" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, bgcolor: '#ede9fe', color: '#6366f1' }} />
                    )}
                    {isNote && (
                      <Chip size="small" icon={<InternalIcon sx={{ fontSize: 12 }} />} label="Internal Note" sx={{ height: 20, fontSize: '0.65rem', fontWeight: 600, bgcolor: '#fef3c7', color: '#d97706' }} />
                    )}
                    <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.72rem' }}>
                      {format(parseISO(item.createdAt), 'MMM d, yyyy h:mm a')}
                    </Typography>
                  </Box>
                  {/* Message */}
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7, color: '#374151', fontSize: '0.88rem' }}>
                    {item.message}
                  </Typography>
                </Paper>
              </Box>
            );
          })}
        </Box>

        {/* Reply Composer */}
        <Box sx={{ px: 3, py: 2, bgcolor: '#fff', borderTop: '1px solid #e5e7eb' }}>
          <TextField
            fullWidth
            multiline
            minRows={2}
            maxRows={6}
            placeholder={isInternalNote ? 'Add an internal note (visible to staff only)...' : 'Type your reply...'}
            value={replyText}
            onChange={(e) => setReplyText(e.target.value)}
            size="small"
            sx={{
              mb: 1.5,
              '& .MuiOutlinedInput-root': {
                borderRadius: '8px',
                borderColor: isInternalNote ? '#fbbf24' : undefined,
                bgcolor: isInternalNote ? '#fffbeb' : '#fff',
              },
            }}
          />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              {isAdmin && (
                <Button
                  size="small"
                  variant={isInternalNote ? 'contained' : 'outlined'}
                  startIcon={<InternalIcon sx={{ fontSize: 16 }} />}
                  onClick={() => setIsInternalNote(!isInternalNote)}
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.78rem',
                    borderRadius: '8px',
                    fontWeight: 500,
                    ...(isInternalNote
                      ? { bgcolor: '#f59e0b', '&:hover': { bgcolor: '#d97706' } }
                      : { borderColor: '#d1d5db', color: '#6b7280' }),
                  }}
                >
                  Internal Note
                </Button>
              )}
            </Box>
            <Button
              variant="contained"
              size="small"
              endIcon={replying ? <CircularProgress size={14} sx={{ color: '#fff' }} /> : <SendIcon sx={{ fontSize: 16 }} />}
              onClick={handleReply}
              disabled={!replyText.trim() || replying}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                borderRadius: '8px',
                bgcolor: isInternalNote ? '#f59e0b' : '#3b82f6',
                boxShadow: 'none',
                px: 2.5,
                '&:hover': { bgcolor: isInternalNote ? '#d97706' : '#2563eb', boxShadow: 'none' },
              }}
            >
              {isInternalNote ? 'Add Note' : 'Send Reply'}
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Right Sidebar - Properties Panel */}
      <Box
        sx={{
          width: 280,
          minWidth: 280,
          bgcolor: '#fff',
          borderLeft: '1px solid #e5e7eb',
          overflow: 'auto',
          py: 2,
        }}
      >
        <Typography variant="caption" fontWeight={600} sx={{ px: 2, mb: 1.5, display: 'block', color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          Ticket Properties
        </Typography>

        {/* Status */}
        <Box sx={{ px: 2, mb: 2 }}>
          <Typography variant="caption" sx={{ color: '#6b7280', mb: 0.5, display: 'block' }}>Status</Typography>
          {isAdmin ? (
            <FormControl size="small" fullWidth>
              <Select
                value={ticket.status}
                onChange={(e) => handleUpdate({ status: e.target.value })}
                sx={{ fontSize: '0.85rem', borderRadius: '8px', height: 36 }}
              >
                {Object.entries(STATUS_LABELS).map(([k, v]) => (
                  <MenuItem key={k} value={k}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <DotIcon sx={{ fontSize: 8, color: STATUS_COLORS[k] }} />
                      {v}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Chip
              size="small"
              label={STATUS_LABELS[ticket.status] || ticket.status}
              sx={{
                fontWeight: 600,
                bgcolor: `${STATUS_COLORS[ticket.status]}15`,
                color: STATUS_COLORS[ticket.status],
              }}
            />
          )}
        </Box>

        {/* Priority */}
        <Box sx={{ px: 2, mb: 2 }}>
          <Typography variant="caption" sx={{ color: '#6b7280', mb: 0.5, display: 'block' }}>Priority</Typography>
          {isAdmin ? (
            <FormControl size="small" fullWidth>
              <Select
                value={ticket.priority}
                onChange={(e) => handleUpdate({ priority: e.target.value })}
                sx={{ fontSize: '0.85rem', borderRadius: '8px', height: 36 }}
              >
                {['urgent', 'high', 'medium', 'low'].map((p) => (
                  <MenuItem key={p} value={p}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: PRIORITY_COLORS[p] }}>
                      {getPriorityIcon(p)}
                      {p.charAt(0).toUpperCase() + p.slice(1)}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: PRIORITY_COLORS[ticket.priority] }}>
              {getPriorityIcon(ticket.priority)}
              <Typography variant="body2" fontWeight={500}>
                {ticket.priority?.charAt(0).toUpperCase() + ticket.priority?.slice(1)}
              </Typography>
            </Box>
          )}
        </Box>

        {/* Category */}
        <Box sx={{ px: 2, mb: 2 }}>
          <Typography variant="caption" sx={{ color: '#6b7280', mb: 0.5, display: 'block' }}>Category</Typography>
          {isAdmin ? (
            <FormControl size="small" fullWidth>
              <Select
                value={ticket.category}
                onChange={(e) => handleUpdate({ category: e.target.value })}
                sx={{ fontSize: '0.85rem', borderRadius: '8px', height: 36 }}
              >
                {CATEGORIES.map((c) => (
                  <MenuItem key={c} value={c}>{c.charAt(0).toUpperCase() + c.slice(1)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          ) : (
            <Typography variant="body2" sx={{ textTransform: 'capitalize', color: '#374151' }}>
              {ticket.category}
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Requester */}
        <Box sx={{ px: 2, mb: 2 }}>
          <Typography variant="caption" sx={{ color: '#6b7280', mb: 0.5, display: 'block' }}>Requester</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Avatar sx={{ width: 24, height: 24, fontSize: '0.65rem', bgcolor: '#3b82f6' }}>
              {(ticket.userName || ticket.requesterName || 'U').charAt(0).toUpperCase()}
            </Avatar>
            <Box>
              <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.82rem', color: '#1f2937' }}>
                {ticket.userName || ticket.requesterName || 'Unknown'}
              </Typography>
              <Typography variant="caption" sx={{ color: '#9ca3af', fontSize: '0.7rem' }}>
                {ticket.userEmail || ticket.requesterEmail || ''}
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Assigned To */}
        <Box sx={{ px: 2, mb: 2 }}>
          <Typography variant="caption" sx={{ color: '#6b7280', mb: 0.5, display: 'block' }}>Assigned To</Typography>
          {ticket.assignedName || ticket.assignedEmail ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Avatar sx={{ width: 24, height: 24, fontSize: '0.65rem', bgcolor: '#6366f1' }}>
                {(ticket.assignedName || 'A').charAt(0).toUpperCase()}
              </Avatar>
              <Box>
                <Typography variant="body2" fontWeight={500} sx={{ fontSize: '0.82rem', color: '#1f2937' }}>
                  {ticket.assignedName || ticket.assignedEmail}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: '#d1d5db', fontStyle: 'italic', fontSize: '0.82rem' }}>
              Unassigned
            </Typography>
          )}
        </Box>

        <Divider sx={{ my: 1.5 }} />

        {/* Dates */}
        <Box sx={{ px: 2, mb: 1.5 }}>
          <Typography variant="caption" sx={{ color: '#6b7280', mb: 0.5, display: 'block' }}>Created</Typography>
          <Typography variant="body2" sx={{ color: '#374151', fontSize: '0.82rem' }}>
            {format(parseISO(ticket.createdAt), 'MMM d, yyyy h:mm a')}
          </Typography>
          <Typography variant="caption" sx={{ color: '#9ca3af' }}>
            {formatDistanceToNow(parseISO(ticket.createdAt), { addSuffix: true })}
          </Typography>
        </Box>

        <Box sx={{ px: 2, mb: 1.5 }}>
          <Typography variant="caption" sx={{ color: '#6b7280', mb: 0.5, display: 'block' }}>Last Updated</Typography>
          <Typography variant="body2" sx={{ color: '#374151', fontSize: '0.82rem' }}>
            {format(parseISO(ticket.updatedAt), 'MMM d, yyyy h:mm a')}
          </Typography>
        </Box>

        {ticket.resolvedAt && (
          <Box sx={{ px: 2, mb: 1.5 }}>
            <Typography variant="caption" sx={{ color: '#6b7280', mb: 0.5, display: 'block' }}>Resolved</Typography>
            <Typography variant="body2" sx={{ color: '#10b981', fontSize: '0.82rem' }}>
              {format(parseISO(ticket.resolvedAt), 'MMM d, yyyy h:mm a')}
            </Typography>
          </Box>
        )}

        {ticket.closedAt && (
          <Box sx={{ px: 2, mb: 1.5 }}>
            <Typography variant="caption" sx={{ color: '#6b7280', mb: 0.5, display: 'block' }}>Closed</Typography>
            <Typography variant="body2" sx={{ color: '#6b7280', fontSize: '0.82rem' }}>
              {format(parseISO(ticket.closedAt), 'MMM d, yyyy h:mm a')}
            </Typography>
          </Box>
        )}

        <Divider sx={{ my: 1.5 }} />

        {/* Stats */}
        <Box sx={{ px: 2, mb: 1 }}>
          <Typography variant="caption" sx={{ color: '#6b7280', mb: 0.5, display: 'block' }}>Replies</Typography>
          <Typography variant="body2" fontWeight={600} sx={{ color: '#374151' }}>
            {replies.length}
          </Typography>
        </Box>

        {ticket.isExternal && (
          <Box sx={{ px: 2, mt: 1 }}>
            <Chip
              size="small"
              label="External Ticket"
              variant="outlined"
              sx={{ fontSize: '0.72rem', borderColor: '#d1d5db', color: '#6b7280' }}
            />
          </Box>
        )}
      </Box>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: '8px' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default HelpdeskTicketDetail;
